import { useEffect, useMemo, useRef, useState } from "react";
import type { KommunStatus, RestriktionTyp } from "@/lib/status";
import { RESTRIKTION_LABEL, formatDate, inForceRows, kommandeRows, worstRestriktionTyp } from "@/lib/status";

const FILL: Record<RestriktionTyp | "ingen" | "okand", string> = {
  forbud: "#e05252",
  rekommendation: "#f0a030",
  ingen: "#4caf84",
  okand: "#9ca3af",
};

const FILL_SELECTED: Record<RestriktionTyp | "ingen" | "okand", string> = {
  forbud: "#c0392b",
  rekommendation: "#c07820",
  ingen: "#2e7d60",
  okand: "#6b7280",
};

// Sweden bounds
const MIN_LON = 10.9;
const MAX_LON = 24.2;
const MIN_LAT = 55.3;
const MAX_LAT = 69.1;

// Center latitude for projection correction (62°N is roughly center of Sweden)
const CENTER_LAT = 62;
const LON_SCALE = Math.cos((CENTER_LAT * Math.PI) / 180);

// Width in "degrees equivalent" (longitude compressed by cos(lat))
const WIDTH_DEG = (MAX_LON - MIN_LON) * LON_SCALE;
const HEIGHT_DEG = MAX_LAT - MIN_LAT;

// Scale factor to make map larger within viewBox
const SCALE = 1.8;

// Maintain proportional aspect ratio (width/height ratio based on scaled degrees)
const VIEWBOX_HEIGHT = 1000 * SCALE;
const VIEWBOX_WIDTH = (WIDTH_DEG / HEIGHT_DEG) * VIEWBOX_HEIGHT;

type Props = {
  rows: KommunStatus[];
  onSelect?: (row: KommunStatus) => void;
  selectedKommunkod?: string | null;
};

type KommunData = {
  rows: KommunStatus[];
  worst: RestriktionTyp | null;
  label: string;
  colorKey: RestriktionTyp | "ingen" | "okand";
};

function projectPoint(lon: number, lat: number): [number, number] {
  // Scale longitude by cos(lat) to account for earth's curvature
  // Use same scale for both axes to maintain proportions
  const x = (((lon - MIN_LON) * LON_SCALE) / WIDTH_DEG) * VIEWBOX_WIDTH;
  const y = ((MAX_LAT - lat) / HEIGHT_DEG) * VIEWBOX_HEIGHT;
  return [x, y];
}

function polygonToPath(coordinates: number[][][]): string {
  let d = "";
  for (const ring of coordinates) {
    if (ring.length === 0) continue;
    const [firstX, firstY] = projectPoint(ring[0][0], ring[0][1]);
    d += `M ${firstX.toFixed(1)} ${firstY.toFixed(1)}`;
    for (let i = 1; i < ring.length; i++) {
      const [x, y] = projectPoint(ring[i][0], ring[i][1]);
      d += ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
    }
    d += " Z ";
  }
  return d.trim();
}

export function SwedenMapSvg({ rows, onSelect, selectedKommunkod }: Props) {
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    fetch("/swedish_municipalities.geojson")
      .then((r) => r.json())
      .then(setGeojson);
  }, []);

  const kommunerByKod = useMemo(() => {
    const map = new Map<string, KommunData>();
    for (const r of rows) {
      const entry = map.get(r.kommunkod) ?? { rows: [], worst: null, label: "", colorKey: "okand" };
      entry.rows.push(r);
      map.set(r.kommunkod, entry);
    }
    map.forEach((entry) => {
      const types = inForceRows(entry.rows)
        .map((r) => r.typ)
        .filter((t): t is RestriktionTyp => t !== null);
      entry.worst = worstRestriktionTyp(types);
      const kommande = kommandeRows(entry.rows);
      const kommandeWorst = worstRestriktionTyp(
        kommande.map((r) => r.typ).filter((t): t is RestriktionTyp => t !== null)
      );
      const kommandeFran = kommande
        .map((r) => r.datum_fran)
        .filter((d): d is string => d !== null)
        .sort()[0];
      const kommandeText = kommandeWorst
        ? `Kommande ${RESTRIKTION_LABEL[kommandeWorst].toLowerCase()}${
            kommandeFran ? ` från ${formatDate(kommandeFran)}` : ""
          }`
        : null;
      if (!entry.worst) {
        const hasSource = entry.rows.some((r) => r.kalla_id);
        entry.label = kommandeText ?? (hasSource ? "Inga restriktioner" : "Ingen data tillgänglig");
        entry.colorKey = hasSource || kommande.length > 0 ? "ingen" : "okand";
      } else {
        entry.label = RESTRIKTION_LABEL[entry.worst] + (kommandeText ? ` · ${kommandeText}` : "");
        entry.colorKey = entry.worst;
      }
    });
    return map;
  }, [rows]);

  const paths = useMemo(() => {
    if (!geojson) return [];
    return geojson.features.map((feature) => {
      const kod = (feature.properties as { id?: string })?.id ?? "";
      const kommunNamn = (feature.properties as { kom_namn?: string })?.kom_namn ?? kod;
      const entry = kommunerByKod.get(kod);
      let d = "";
      if (feature.geometry.type === "Polygon") {
        d = polygonToPath((feature.geometry as GeoJSON.Polygon).coordinates);
      } else if (feature.geometry.type === "MultiPolygon") {
        const multi = feature.geometry as GeoJSON.MultiPolygon;
        d = multi.coordinates.map((poly) => polygonToPath(poly)).join(" ");
      }
      const isSelected = kod === selectedKommunkod;
      const colorKey = entry?.colorKey ?? "okand";
      const fill = isSelected ? FILL_SELECTED[colorKey] : FILL[colorKey];
      return {
        kod,
        kommunNamn,
        d,
        fill,
        entry,
        isSelected,
      };
    });
  }, [geojson, kommunerByKod, selectedKommunkod]);

  const handleMouseEnter = (e: React.MouseEvent, p: typeof paths[0]) => {
    const text = p.entry ? `${p.kommunNamn}: ${p.entry.label}` : `${p.kommunNamn}: Ingen data tillgänglig`;
    setTooltip({ x: e.clientX, y: e.clientY - 40, text });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setTooltip((t) => (t ? { ...t, x: e.clientX, y: e.clientY - 40 } : null));
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  const handleClick = (p: typeof paths[0]) => {
    if (p.entry?.rows[0]) {
      onSelect?.(p.entry.rows[0]);
    }
  };

  if (!geojson) {
    return (
      <div className="w-full h-[55vh] sm:h-[70vh] lg:h-[90vh] animate-pulse bg-muted/40" />
    );
  }

  return (
    <div className="relative w-full h-[55vh] sm:h-[70vh] lg:h-[90vh]">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        className="w-full h-full cursor-pointer"
        role="region"
        aria-label="Karta över Sverige med bevattningsförbud"
        onMouseMove={handleMouseMove}
      >
        {paths.map((p) => (
          <path
            key={p.kod}
            d={p.d}
            fill={p.fill}
            stroke={p.isSelected ? "#fff" : "#fff"}
            strokeWidth={p.isSelected ? 2.5 : 0.8}
            onMouseEnter={(e) => handleMouseEnter(e, p)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleClick(p)}
            className="transition-colors duration-150"
            style={{ pointerEvents: "all" }}
          />
        ))}
      </svg>
      {tooltip && (
        <div
          className="fixed z-50 px-3 py-2 text-sm bg-white rounded-lg shadow-lg border border-border pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y, transform: "translate(-50%, 0)" }}
          dangerouslySetInnerHTML={{ __html: tooltip.text.replace(/: /g, "<br/>") }}
        />
      )}
    </div>
  );
}
